<template>
  <div
    class="progress-circle-container"
    :class="`size-${size}`"
    :style="{
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`,
    }"
    role="progressbar"
    :aria-valuenow="percentage"
    aria-valuemin="0"
    aria-valuemax="100"
  >
    <svg class="progress-circle" viewBox="0 0 100 100">
      <circle
        class="progress-circle-bg"
        cx="50"
        cy="50"
        r="46"
        fill="none"
        stroke="#E6E6E6"
        stroke-width="8"
      />
      <circle
        class="progress-circle-arc"
        cx="50"
        cy="50"
        r="46"
        fill="none"
        stroke="#492B7C"
        stroke-width="8"
        stroke-linecap="round"
        :stroke-dasharray="`${dashArray} ${dashArray}`"
        :stroke-dashoffset="dashOffset"
        transform="rotate(-90, 50, 50)"
      />
    </svg>

    <div class="progress-text">{{ percentage }}%</div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';

export default defineComponent({
  name: 'ProgressCircle',
  props: {
    percentage: {
      type: Number,
      default: 0,
      validator: (value: number) => value >= 0 && value <= 100,
    },
    size: {
      type: String,
      default: 'large',
      validator: (value: string) =>
        ['small', 'medium', 'large'].includes(value.toLowerCase()),
    },
  },
  setup(props) {
    const dimensions = computed(() => {
      switch (props.size.toLowerCase()) {
        case 'small':
          return { width: 50, height: 50 };
        case 'medium':
          return { width: 64, height: 64 };
        case 'large':
          return { width: 84, height: 84 };
        default:
          return { width: 64, height: 64 };
      }
    });

    const dashArray = computed(() => 2 * Math.PI * 46);
    const dashOffset = computed(
      () => dashArray.value * (1 - props.percentage / 100),
    );

    return {
      dimensions,
      dashArray,
      dashOffset,
    };
  },
});
</script>

<style scoped>
.progress-circle-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.progress-circle {
  width: 100%;
  height: 100%;
}

.progress-circle-arc {
  transition: stroke-dashoffset 0.3s ease;
}

.progress-text {
  position: absolute;
  font-family: 'Jost', sans-serif;
  color: #000000;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.size-small .progress-text {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0px;
}

.size-medium .progress-text {
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
}

.size-large .progress-text {
  font-weight: 500;
  font-size: 18px;
  line-height: 28px;
  letter-spacing: 0px;
}
</style>
